// ─── Security Master – Dynamic Loader ──────────────────────────────────────
// Fetches securities from multiple free-tier APIs and caches them locally.
//
// Data sources:
//   Crypto    (10 000-15 000) – CoinGecko public API  (no key required)
//                               Coinpaprika fallback when CoinGecko rate-limits
//   US Stocks  (5 000+)       – Finnhub free tier      (REACT_APP_FINNHUB_KEY)
//   US Stocks  (5 000+)       – Polygon.io free tier   (REACT_APP_POLYGON_KEY, supplemental)
//   Int'l     (40 000-50 000) – Twelve Data free tier  (REACT_APP_TWELVE_DATA_KEY, deduped)
//   OTC       (10 000-12 000) – FMP free tier           (REACT_APP_FMP_KEY, incl. Pink Sheets)
//   FX        (20-30 pairs)   – Static seed data
//   Commodities (15-20)       – Static seed data
//
// Each category is lazy-loaded and cached; the app shows seed data
// immediately while background fetches complete.

// Import and re-export canonical type definitions from src/types/security.ts
import type {
  Security,
  CategoryKey,
  LoadState,
  CategoryStatus,
  SecurityMasterStatus,
} from '../types/security';

export type {
  Security,
  CategoryKey,
  LoadState,
  CategoryStatus,
  SecurityMasterStatus,
};

// ── API key helpers ────────────────────────────────────────────────────────
const FINNHUB_KEY      = process.env.REACT_APP_FINNHUB_KEY      ?? '';
const POLYGON_KEY      = process.env.REACT_APP_POLYGON_KEY      ?? '';
const TWELVE_DATA_KEY  = process.env.REACT_APP_TWELVE_DATA_KEY  ?? '';
const FMP_KEY          = process.env.REACT_APP_FMP_KEY          ?? '';

// ── CORS proxy (used as fallback when the browser blocks a direct API call) ──
// Format: https://corsproxy.io/?url=<encoded-target-url>
const CORS_PROXY = 'https://corsproxy.io/?url=';

// ── Cache configuration ────────────────────────────────────────────────────
const CACHE_PREFIX = 'sm_v3_';
const CACHE_TTL: Record<CategoryKey, number> = {
  crypto:        60 * 60 * 1000,        // 1 h
  usStocks:      24 * 60 * 60 * 1000,   // 24 h
  international: 24 * 60 * 60 * 1000,
  otc:           24 * 60 * 60 * 1000,
};

// ── localStorage cache ─────────────────────────────────────────────────────
interface StoredEntry {
  data: Security[];
  ts: number;
}

function lsGet(key: string, ttlMs: number): Security[] | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: StoredEntry = JSON.parse(raw);
    if (Date.now() - entry.ts > ttlMs) return null;
    return entry.data;
  } catch {
    return null;
  }
}

function lsSet(key: string, data: Security[]): void {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // Quota exceeded – evict all sm_ entries then retry once
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith(CACHE_PREFIX))
        .forEach(k => localStorage.removeItem(k));
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ data, ts: Date.now() }));
    } catch { /* silently ignore */ }
  }
}

// ── IndexedDB cache (for large datasets >5 000 entries) ───────────────────
const IDB_DB_NAME    = 'SecurityMasterDB_v1';
const IDB_STORE_NAME = 'data';

function idbOpen(): Promise<IDBDatabase | null> {
  return new Promise(resolve => {
    try {
      const req = indexedDB.open(IDB_DB_NAME, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(IDB_STORE_NAME, { keyPath: 'key' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror   = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbGet(key: string, ttlMs: number): Promise<Security[] | null> {
  try {
    const db = await idbOpen();
    if (!db) return null;
    return new Promise(resolve => {
      const tx  = db.transaction(IDB_STORE_NAME, 'readonly');
      const req = tx.objectStore(IDB_STORE_NAME).get(key);
      req.onsuccess = () => {
        const r = req.result;
        if (!r || Date.now() - r.ts > ttlMs) resolve(null);
        else resolve(r.data as Security[]);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function idbSet(key: string, data: Security[]): Promise<void> {
  try {
    const db = await idbOpen();
    if (!db) return;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE_NAME, 'readwrite');
      tx.objectStore(IDB_STORE_NAME).put({ key, data, ts: Date.now() });
      tx.oncomplete = () => resolve();
      tx.onerror    = () => reject(tx.error);
    });
  } catch { /* silently ignore */ }
}

// ── Seed data (offline / API-unavailable fallback) ─────────────────────────
// Kept as a representative set across all categories so the UI is never empty.
export const SEED_SECURITIES: Security[] = [
  // Crypto/blockchain stocks
  { ticker: 'COIN',  name: 'Coinbase Global',              exchange: 'NASDAQ',   lastTrade: 195.53, change:  2.30,   changePercent:  1.19,  volume: 11940000,  sharesOut: 223040000,   marketCap: 43610000000,    category: 'Stock' },
  { ticker: 'IREN',  name: 'Iren Ltd',                     exchange: 'NASDAQ',   lastTrade:  41.57, change:  0.20,   changePercent:  0.48,  volume: 34040000,  sharesOut: 331760000,   marketCap: 13790000000,    category: 'Stock' },
  { ticker: 'WULF',  name: 'TeraWulf Inc',                 exchange: 'NASDAQ',   lastTrade:  14.67, change:  0.00,   changePercent:  0.00,  volume: 27370000,  sharesOut: 418680000,   marketCap:  6140000000,    category: 'Stock' },
  { ticker: 'GLXY',  name: 'Galaxy Digital Holdings',      exchange: 'TSX',      lastTrade:  30.72, change:  2.58,   changePercent:  9.17,  volume:  1030000,  sharesOut: 190690000,   marketCap:  5860000000,    category: 'Stock' },
  { ticker: 'CIFR',  name: 'Cipher Mining Inc',            exchange: 'NASDAQ',   lastTrade:  14.08, change:  0.37,   changePercent:  2.70,  volume: 31170000,  sharesOut: 395090000,   marketCap:  5560000000,    category: 'Stock' },
  { ticker: 'HUT',   name: 'Hut 8 Corp',                   exchange: 'NASDAQ',   lastTrade:  48.32, change: -0.67,   changePercent: -1.37,  volume:  2680000,  sharesOut: 108040000,   marketCap:  5220000000,    category: 'Stock' },
  { ticker: 'RIOT',  name: 'Riot Platforms Inc',           exchange: 'NASDAQ',   lastTrade:  14.04, change: -3.63,   changePercent:-20.54,  volume: 19650000,  sharesOut: 371810000,   marketCap:  5220000000,    category: 'Stock' },
  { ticker: 'CORZ',  name: 'Core Scientific Inc',          exchange: 'NASDAQ',   lastTrade:  16.49, change:  0.25,   changePercent:  1.54,  volume: 10110000,  sharesOut: 310060000,   marketCap:  5110000000,    category: 'Stock' },
  { ticker: 'MARA',  name: 'Mara Holdings Inc',            exchange: 'NASDAQ',   lastTrade:   9.32, change:  0.56,   changePercent:  6.39,  volume: 72710000,  sharesOut: 378180000,   marketCap:  3520000000,    category: 'Stock' },
  { ticker: 'CLSK',  name: 'CleanSpark Inc',               exchange: 'NASDAQ',   lastTrade:   9.76, change:  0.21,   changePercent:  2.20,  volume: 23230000,  sharesOut: 255750000,   marketCap:  2500000000,    category: 'Stock' },
  { ticker: 'BITF',  name: 'Bitfarms Ltd',                 exchange: 'NASDAQ',   lastTrade:   2.24, change:  0.02,   changePercent:  0.90,  volume: 30610000,  sharesOut: 597920000,   marketCap:  1340000000,    category: 'Stock' },
  { ticker: 'BTDR',  name: 'Bitdeer Technologies Group',   exchange: 'NASDAQ',   lastTrade:   7.50, change:  0.16,   changePercent:  2.18,  volume:  5220000,  sharesOut: 177340000,   marketCap:  1330000000,    category: 'Stock' },
  { ticker: 'DEFTF', name: 'DeFi Technologies Inc',        exchange: 'OTC',      lastTrade:   3.07, change:  0.00,   changePercent:  0.00,  volume:         0, sharesOut: 327070000,   marketCap:  1000000000,    category: 'OTC'   },
  { ticker: 'HIVE',  name: 'Hive Digital Technologies',    exchange: 'NASDAQ',   lastTrade:   2.13, change:  0.05,   changePercent:  2.40,  volume: 10920000,  sharesOut: 249200000,   marketCap:   530800000,    category: 'Stock' },
  { ticker: 'BTBT',  name: 'Bit Digital Inc',              exchange: 'NASDAQ',   lastTrade:   1.62, change: -0.315,  changePercent:-16.28,  volume: 15850000,  sharesOut: 323670000,   marketCap:   522730000,    category: 'Stock' },
  { ticker: 'MSTR',  name: 'MicroStrategy Inc',            exchange: 'NASDAQ',   lastTrade: 345.32, change: 12.40,   changePercent:  3.73,  volume:  4890000,  sharesOut: 193000000,   marketCap:  66700000000,   category: 'Stock' },
  // Major tech
  { ticker: 'AAPL',  name: 'Apple Inc',                    exchange: 'NASDAQ',   lastTrade: 228.87, change:  1.24,   changePercent:  0.54,  volume: 55120000,  sharesOut: 15204137000,  marketCap: 3498000000000,  category: 'Stock' },
  { ticker: 'MSFT',  name: 'Microsoft Corp',               exchange: 'NASDAQ',   lastTrade: 415.51, change: -1.09,   changePercent: -0.26,  volume: 18340000,  sharesOut:  7432000000,  marketCap: 3086000000000,  category: 'Stock' },
  { ticker: 'NVDA',  name: 'NVIDIA Corp',                  exchange: 'NASDAQ',   lastTrade: 121.44, change: -3.26,   changePercent: -2.61,  volume:193450000,  sharesOut: 24444000000,  marketCap: 2970000000000,  category: 'Stock' },
  { ticker: 'GOOGL', name: 'Alphabet Inc Class A',         exchange: 'NASDAQ',   lastTrade: 193.27, change:  0.88,   changePercent:  0.46,  volume: 21630000,  sharesOut: 12180000000,  marketCap: 2354000000000,  category: 'Stock' },
  { ticker: 'AMZN',  name: 'Amazon.com Inc',               exchange: 'NASDAQ',   lastTrade: 219.96, change: -1.52,   changePercent: -0.69,  volume: 34560000,  sharesOut: 10660000000,  marketCap: 2346000000000,  category: 'Stock' },
  { ticker: 'META',  name: 'Meta Platforms Inc',           exchange: 'NASDAQ',   lastTrade: 601.81, change:  4.31,   changePercent:  0.72,  volume: 14280000,  sharesOut:  2536000000,  marketCap: 1526000000000,  category: 'Stock' },
  { ticker: 'TSLA',  name: 'Tesla Inc',                    exchange: 'NASDAQ',   lastTrade: 277.97, change: -8.44,   changePercent: -2.95,  volume: 98730000,  sharesOut:  3210000000,  marketCap:  892400000000,  category: 'Stock' },
  { ticker: 'AMD',   name: 'Advanced Micro Devices Inc',   exchange: 'NASDAQ',   lastTrade: 117.83, change: -2.76,   changePercent: -2.28,  volume: 45670000,  sharesOut:  1618000000,  marketCap:  190700000000,  category: 'Stock' },
  { ticker: 'PLTR',  name: 'Palantir Technologies Inc',    exchange: 'NASDAQ',   lastTrade:  80.47, change:  1.13,   changePercent:  1.43,  volume: 61540000,  sharesOut:  2136000000,  marketCap:  171900000000,  category: 'Stock' },
  { ticker: 'JPM',   name: 'JPMorgan Chase & Co',          exchange: 'NYSE',     lastTrade: 266.23, change:  1.47,   changePercent:  0.55,  volume: 10340000,  sharesOut:  2860000000,  marketCap:  761300000000,  category: 'Stock' },
  { ticker: 'V',     name: 'Visa Inc',                     exchange: 'NYSE',     lastTrade: 340.21, change:  0.88,   changePercent:  0.26,  volume:  5780000,  sharesOut:  2036000000,  marketCap:  692500000000,  category: 'Stock' },
  { ticker: 'WMT',   name: 'Walmart Inc',                  exchange: 'NYSE',     lastTrade:  92.44, change:  0.34,   changePercent:  0.37,  volume: 18760000,  sharesOut:  8019000000,  marketCap:  741400000000,  category: 'Stock' },
  { ticker: 'LLY',   name: 'Eli Lilly & Co',               exchange: 'NYSE',     lastTrade: 888.37, change: -4.22,   changePercent: -0.47,  volume:  2830000,  sharesOut:   952000000,  marketCap:  845600000000,  category: 'Stock' },
  { ticker: 'XOM',   name: 'Exxon Mobil Corp',             exchange: 'NYSE',     lastTrade: 109.76, change:  0.55,   changePercent:  0.50,  volume: 15640000,  sharesOut:  3990000000,  marketCap:  437900000000,  category: 'Stock' },
  // ETFs
  { ticker: 'SPY',   name: 'SPDR S&P 500 ETF Trust',       exchange: 'NYSE',     lastTrade: 566.09, change:  2.34,   changePercent:  0.41,  volume: 49870000,  sharesOut:   924000000,  marketCap:  523000000000,  category: 'ETF'   },
  { ticker: 'QQQ',   name: 'Invesco QQQ Trust',             exchange: 'NASDAQ',   lastTrade: 484.53, change:  1.78,   changePercent:  0.37,  volume: 33240000,  sharesOut:   561000000,  marketCap:  271800000000,  category: 'ETF'   },
  { ticker: 'IWM',   name: 'iShares Russell 2000 ETF',      exchange: 'NYSE',     lastTrade: 216.43, change: -0.91,   changePercent: -0.42,  volume: 22130000,  sharesOut:   311000000,  marketCap:   67200000000,  category: 'ETF'   },
  { ticker: 'GLD',   name: 'SPDR Gold Shares ETF',          exchange: 'NYSE',     lastTrade: 258.74, change:  1.22,   changePercent:  0.47,  volume:  8670000,  sharesOut:   385000000,  marketCap:   99600000000,  category: 'ETF'   },
  { ticker: 'VTI',   name: 'Vanguard Total Stock Market ETF', exchange: 'NYSE',  lastTrade: 278.44, change:  1.01,   changePercent:  0.36,  volume:  4230000,  sharesOut:   792000000,  marketCap:  220500000000,  category: 'ETF'   },
  { ticker: 'IBIT',  name: 'iShares Bitcoin Trust',         exchange: 'NASDAQ',   lastTrade:  54.12, change:  1.34,   changePercent:  2.54,  volume: 55240000,  sharesOut:   570000000,  marketCap:   30850000000,  category: 'ETF'   },
  { ticker: 'GBTC',  name: 'Grayscale Bitcoin Trust',       exchange: 'NYSE',     lastTrade:  57.33, change:  1.41,   changePercent:  2.52,  volume: 12450000,  sharesOut:   488000000,  marketCap:   27980000000,  category: 'ETF'   },
  // Crypto tokens
  { ticker: 'BTC',   name: 'Bitcoin',                       exchange: 'Crypto',   lastTrade: 83400.00, change: 1850.00, changePercent:  2.27, volume: 38400000000, sharesOut:  19730000,  marketCap: 1645000000000,  category: 'Crypto' },
  { ticker: 'ETH',   name: 'Ethereum',                      exchange: 'Crypto',   lastTrade:  1970.00, change:   48.50, changePercent:  2.52, volume: 15600000000, sharesOut: 120420000,  marketCap:  237400000000,  category: 'Crypto' },
  { ticker: 'BNB',   name: 'BNB',                           exchange: 'Crypto',   lastTrade:   612.40, change:   11.20, changePercent:  1.86, volume:  1420000000, sharesOut: 145888888,  marketCap:   89360000000,  category: 'Crypto' },
  { ticker: 'SOL',   name: 'Solana',                        exchange: 'Crypto',   lastTrade:   130.50, change:    4.20, changePercent:  3.33, volume:  4280000000, sharesOut: 595500000,  marketCap:   77720000000,  category: 'Crypto' },
  { ticker: 'XRP',   name: 'XRP',                           exchange: 'Crypto',   lastTrade:     2.41, change:    0.06, changePercent:  2.55, volume:  4130000000, sharesOut: 57420000000, marketCap: 138380000000,  category: 'Crypto' },
  { ticker: 'DOGE',  name: 'Dogecoin',                      exchange: 'Crypto',   lastTrade:    0.184, change:   0.006, changePercent:  3.37, volume:  1870000000, sharesOut: 144800000000, marketCap: 26640000000,  category: 'Crypto' },
  { ticker: 'ADA',   name: 'Cardano',                       exchange: 'Crypto',   lastTrade:     0.75, change:    0.02, changePercent:  2.74, volume:   620000000, sharesOut: 35690000000, marketCap:  26770000000,  category: 'Crypto' },
  { ticker: 'LINK',  name: 'Chainlink',                     exchange: 'Crypto',   lastTrade:    13.74, change:    0.31, changePercent:  2.31, volume:   310000000, sharesOut:  631000000,  marketCap:   8670000000,  category: 'Crypto' },
  { ticker: 'AVAX',  name: 'Avalanche',                     exchange: 'Crypto',   lastTrade:    21.34, change:    0.57, changePercent:  2.75, volume:   290000000, sharesOut:  417000000,  marketCap:   8899000000,  category: 'Crypto' },
  // OTC samples
  { ticker: 'BTCS',  name: 'BTCS Inc',                      exchange: 'OTC',      lastTrade:   1.73, change: -0.01,   changePercent: -0.58,  volume:   759907,  sharesOut:  47150000,   marketCap:    81570000,    category: 'OTC'   },
  { ticker: 'ARBKF', name: 'Argo Blockchain PLC',           exchange: 'OTC',      lastTrade:  0.003, change:  0.00,   changePercent:  0.00,  volume:         0, sharesOut: 720660000,   marketCap:     2160000,    category: 'OTC'   },
];

// ── Static FX pairs (always available, no API key needed) ─────────────────
export const FX_SECURITIES: Security[] = [
  { ticker: 'EUR/USD', name: 'Euro / US Dollar',              exchange: 'FX', lastTrade:  1.0872, change:  0.0014, changePercent:  0.13, volume: 5800000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'GBP/USD', name: 'British Pound / US Dollar',     exchange: 'FX', lastTrade:  1.2945, change:  0.0021, changePercent:  0.16, volume: 2400000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'USD/JPY', name: 'US Dollar / Japanese Yen',      exchange: 'FX', lastTrade: 148.640, change: -0.380,  changePercent: -0.26, volume: 2900000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'USD/CHF', name: 'US Dollar / Swiss Franc',       exchange: 'FX', lastTrade:  0.8981, change: -0.0010, changePercent: -0.11, volume: 1800000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'AUD/USD', name: 'Australian Dollar / US Dollar', exchange: 'FX', lastTrade:  0.6512, change:  0.0008, changePercent:  0.12, volume: 1600000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'USD/CAD', name: 'US Dollar / Canadian Dollar',   exchange: 'FX', lastTrade:  1.3645, change:  0.0015, changePercent:  0.11, volume: 1500000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'NZD/USD', name: 'New Zealand Dollar / US Dollar',exchange: 'FX', lastTrade:  0.5981, change:  0.0006, changePercent:  0.10, volume:  800000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'EUR/GBP', name: 'Euro / British Pound',          exchange: 'FX', lastTrade:  0.8396, change: -0.0009, changePercent: -0.11, volume:  900000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'EUR/JPY', name: 'Euro / Japanese Yen',           exchange: 'FX', lastTrade: 161.70,  change: -0.210,  changePercent: -0.13, volume: 1200000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'EUR/CHF', name: 'Euro / Swiss Franc',            exchange: 'FX', lastTrade:  0.9761, change: -0.0008, changePercent: -0.08, volume:  700000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'EUR/AUD', name: 'Euro / Australian Dollar',      exchange: 'FX', lastTrade:  1.6695, change:  0.0018, changePercent:  0.11, volume:  600000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'EUR/CAD', name: 'Euro / Canadian Dollar',        exchange: 'FX', lastTrade:  1.4823, change:  0.0020, changePercent:  0.14, volume:  600000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'GBP/JPY', name: 'British Pound / Japanese Yen', exchange: 'FX', lastTrade: 192.60,  change: -0.250,  changePercent: -0.13, volume: 1000000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'GBP/CHF', name: 'British Pound / Swiss Franc',   exchange: 'FX', lastTrade:  1.1631, change: -0.0012, changePercent: -0.10, volume:  500000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'AUD/JPY', name: 'Australian Dollar / Japanese Yen', exchange: 'FX', lastTrade: 96.87, change: -0.120, changePercent: -0.12, volume:  700000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'CHF/JPY', name: 'Swiss Franc / Japanese Yen',    exchange: 'FX', lastTrade: 165.53,  change: -0.180,  changePercent: -0.11, volume:  400000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'CAD/JPY', name: 'Canadian Dollar / Japanese Yen',exchange: 'FX', lastTrade: 109.00,  change: -0.140,  changePercent: -0.13, volume:  450000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'NZD/JPY', name: 'New Zealand Dollar / Japanese Yen', exchange: 'FX', lastTrade: 88.91, change: -0.110, changePercent: -0.12, volume: 350000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'AUD/CHF', name: 'Australian Dollar / Swiss Franc', exchange: 'FX', lastTrade: 0.5849, change: -0.0006, changePercent: -0.10, volume: 300000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'AUD/CAD', name: 'Australian Dollar / Canadian Dollar', exchange: 'FX', lastTrade: 0.8884, change: -0.0008, changePercent: -0.09, volume: 350000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'EUR/NZD', name: 'Euro / New Zealand Dollar',     exchange: 'FX', lastTrade:  1.8178, change:  0.0022, changePercent:  0.12, volume:  350000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'GBP/AUD', name: 'British Pound / Australian Dollar', exchange: 'FX', lastTrade: 1.9878, change: 0.0025, changePercent: 0.13, volume: 400000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'GBP/NZD', name: 'British Pound / New Zealand Dollar', exchange: 'FX', lastTrade: 2.1650, change: 0.0028, changePercent: 0.13, volume: 300000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'GBP/CAD', name: 'British Pound / Canadian Dollar', exchange: 'FX', lastTrade: 1.7660, change: 0.0022, changePercent: 0.12, volume: 350000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'USD/HKD', name: 'US Dollar / Hong Kong Dollar',  exchange: 'FX', lastTrade:  7.8242, change:  0.0003, changePercent:  0.00, volume:  600000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'USD/SGD', name: 'US Dollar / Singapore Dollar',  exchange: 'FX', lastTrade:  1.3476, change:  0.0010, changePercent:  0.07, volume:  500000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'USD/MXN', name: 'US Dollar / Mexican Peso',      exchange: 'FX', lastTrade: 17.1580, change:  0.0350, changePercent:  0.20, volume:  700000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'USD/ZAR', name: 'US Dollar / South African Rand',exchange: 'FX', lastTrade: 18.6520, change:  0.0420, changePercent:  0.23, volume:  400000000, sharesOut: 0, marketCap: 0, category: 'FX' },
  { ticker: 'USD/CNH', name: 'US Dollar / Chinese Renminbi',  exchange: 'FX', lastTrade:  7.2350, change:  0.0040, changePercent:  0.06, volume:  800000000, sharesOut: 0, marketCap: 0, category: 'FX' },
];

// ── Static Commodity entries (always available, no API key needed) ─────────
export const COMMODITY_SECURITIES: Security[] = [
  { ticker: 'XAUUSD',   name: 'Gold Spot',                  exchange: 'Commodity', lastTrade: 2345.50, change:  8.40, changePercent:  0.36, volume: 185000000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'XAGUSD',   name: 'Silver Spot',                exchange: 'Commodity', lastTrade:   27.82, change:  0.18, changePercent:  0.65, volume:  15000000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'WTICOUSD', name: 'WTI Crude Oil',              exchange: 'Commodity', lastTrade:   78.50, change: -0.62, changePercent: -0.78, volume:  42000000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'BCOUSD',   name: 'Brent Crude Oil',            exchange: 'Commodity', lastTrade:   82.10, change: -0.55, changePercent: -0.67, volume:  38000000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'NATGASUSD',name: 'Natural Gas',                exchange: 'Commodity', lastTrade:    2.95, change:  0.04, changePercent:  1.37, volume:  18000000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'XCUUSD',   name: 'Copper',                     exchange: 'Commodity', lastTrade:    4.12, change:  0.03, changePercent:  0.73, volume:  12000000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'CORN',     name: 'Corn Futures',               exchange: 'Commodity', lastTrade:  438.25, change: -2.75, changePercent: -0.62, volume:   8000000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'WHEAT',    name: 'Wheat Futures',              exchange: 'Commodity', lastTrade:  563.50, change:  4.25, changePercent:  0.76, volume:   6000000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'SOYBN',    name: 'Soybean Futures',            exchange: 'Commodity', lastTrade: 1205.75, change: -7.25, changePercent: -0.60, volume:   7000000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'COFFEE',   name: 'Coffee Arabica Futures',     exchange: 'Commodity', lastTrade:  198.40, change:  2.10, changePercent:  1.07, volume:   3500000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'SUGAR',    name: 'Sugar #11 Futures',          exchange: 'Commodity', lastTrade:   21.85, change: -0.15, changePercent: -0.68, volume:   4200000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'COTTON',   name: 'Cotton Futures',             exchange: 'Commodity', lastTrade:   82.30, change:  0.45, changePercent:  0.55, volume:   2800000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'XPTUSD',   name: 'Platinum Spot',              exchange: 'Commodity', lastTrade: 1028.40, change:  5.20, changePercent:  0.51, volume:   4500000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'XPDUSD',   name: 'Palladium Spot',             exchange: 'Commodity', lastTrade:  980.20, change: -8.30, changePercent: -0.84, volume:   3800000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'ALUM',     name: 'Aluminum',                   exchange: 'Commodity', lastTrade: 2280.00, change: 12.50, changePercent:  0.55, volume:   9000000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'NICKEL',   name: 'Nickel',                     exchange: 'Commodity', lastTrade:16850.00, change:-95.00, changePercent: -0.56, volume:   5500000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'COCOA',    name: 'Cocoa Futures',              exchange: 'Commodity', lastTrade: 8240.00, change: 65.00, changePercent:  0.79, volume:   2200000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'OJ',       name: 'Orange Juice Futures',       exchange: 'Commodity', lastTrade:  385.00, change:  4.20, changePercent:  1.10, volume:    900000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
  { ticker: 'LUMBER',   name: 'Random Length Lumber',       exchange: 'Commodity', lastTrade:  542.00, change: -3.50, changePercent: -0.64, volume:    600000000, sharesOut: 0, marketCap: 0, category: 'Commodity' },
];

// ── Coinpaprika ticker response shape ─────────────────────────────────────
interface CoinpaprikaQuoteUSD {
  price:              number;
  volume_24h:         number;
  market_cap:         number;
  percent_change_24h: number;
}
interface CoinpaprikaTicker {
  symbol:              string;
  name:                string;
  circulating_supply:  number;
  quotes: { USD?: CoinpaprikaQuoteUSD };
}

// ── Singleton Loader ───────────────────────────────────────────────────────
type StatusListener = (status: SecurityMasterStatus) => void;

class SecurityMasterLoader {
  private data: Record<CategoryKey, Security[]> = {
    crypto: [], usStocks: [], international: [], otc: [],
  };

  private status: SecurityMasterStatus = {
    crypto:        { state: 'idle', count: 0 },
    usStocks:      { state: 'idle', count: 0 },
    international: { state: 'idle', count: 0 },
    otc:           { state: 'idle', count: 0 },
    totalCount: 0,
  };

  private listeners = new Set<StatusListener>();
  private initialized = false;
  private logged70k = false;
  private readonly COINGECKO_MAX_PAGES    = 40;   // 40 × 250 = 10,000 coins
  private readonly COINGECKO_DELAY_MS     = 7000; // ~8 req/min – within free-tier limit
  private readonly MIN_CRYPTO_THRESHOLD   = 1000; // minimum coins before considering the category populated

  subscribe(fn: StatusListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    const total = (Object.values(this.data) as Security[][]).reduce((s, a) => s + a.length, 0);
    this.status = { ...this.status, totalCount: total };
    if (!this.logged70k && total >= 70000) {
      this.logged70k = true;
      console.log(
        `[SecurityMaster] ✅ Target reached: ${total.toLocaleString()} securities loaded`,
        this.getStatus(),
      );
    }
    this.listeners.forEach(fn => fn({ ...this.status }));
  }

  private updateCategory(cat: CategoryKey, patch: Partial<CategoryStatus>) {
    this.status = { ...this.status, [cat]: { ...this.status[cat], ...patch } };
    if (patch.state === 'complete') {
      console.log(
        `[SecurityMaster] ${cat} complete: ${this.status[cat].count.toLocaleString()} securities`,
      );
    }
    this.emit();
  }

  getStatus(): SecurityMasterStatus { return { ...this.status }; }

  /** Return all loaded securities, falling back to seed data if nothing loaded yet.
   *  FX and Commodity static lists are always appended regardless of API load state. */
  getAll(): Security[] {
    // Use .flat() instead of spread (...list) to avoid "Maximum call stack size exceeded"
    // when the combined list exceeds ~100k items (spread passes each element as a
    // function argument, which exhausts the JS call stack at large array sizes).
    const combined = (Object.values(this.data) as Security[][]).flat();
    // Use Array.from for the seed fallback too – avoids spread on an unknown-size array.
    const base = combined.length > 0 ? combined : Array.from(SEED_SECURITIES);
    // FX and Commodity are always available as static data independent of API loads.
    return [...base, ...FX_SECURITIES, ...COMMODITY_SECURITIES];
  }

  /** Return securities for a specific display category (e.g. 'Crypto', 'OTC'). */
  getByCategory(category: string): Security[] {
    if (category === 'All') return this.getAll();

    // FX and Commodity are always served from their static arrays.
    if (category === 'FX')        return FX_SECURITIES;
    if (category === 'Commodity') return COMMODITY_SECURITIES;

    // Map display-level categories to internal loader buckets for efficiency
    const categoryToKey: Record<string, CategoryKey | null> = {
      Crypto:        'crypto',
      Stock:         'usStocks',
      ETF:           'usStocks',
      International: 'international',
      OTC:           'otc',
    };
    const key = categoryToKey[category];
    if (key !== undefined) {
      const bucket = this.data[key as CategoryKey];
      // For 'Stock'/'ETF', further filter within the usStocks bucket
      const list   = bucket.length > 0 ? bucket : SEED_SECURITIES.filter(s => s.category === category);
      if (key === 'usStocks' && (category === 'Stock' || category === 'ETF')) {
        return list.filter(s => s.category === category);
      }
      return list;
    }

    // Fallback: filter across all loaded data (covers FX and other custom categories)
    return this.getAll().filter(s => s.category === category);
  }

  // ── Public init ──────────────────────────────────────────────────────────
  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    // 1. Restore from caches (fast, no network round-trip)
    await Promise.all([
      this.restoreFromCache('crypto'),
      this.restoreFromCache('usStocks'),
      this.restoreFromCache('international'),
      this.restoreFromCache('otc'),
    ]);

    // 2. Start background API loads (fire-and-forget)
    this.loadCrypto().catch(() => { /* handled inside */ });
    // Run Finnhub first, then start Polygon so that Polygon's dedup set is
    // already seeded with Finnhub's tickers and the two loaders don't race
    // to overwrite each other's partial results in this.data.usStocks.
    // Use `finally` (not `then`) so Polygon still loads even if Finnhub fails.
    const finnhubLoad = FINNHUB_KEY
      ? this.loadUSStocks()
      : Promise.resolve();
    finnhubLoad
      .catch(() => { /* handled inside loadUSStocks */ })
      .finally(() => {
        if (POLYGON_KEY) this.loadPolygonStocks().catch(() => { /* handled inside */ });
      });
    if (TWELVE_DATA_KEY) this.loadInternational().catch(() => { /* handled inside */ });
    if (FMP_KEY)         this.loadOTC().catch(() => { /* handled inside */ });
  }

  /** Trigger a forced refresh of one or all categories. */
  forceRefresh(cat?: CategoryKey): void {
    if (cat) {
      this.data[cat] = [];
      this.updateCategory(cat, { state: 'idle', count: 0, lastUpdated: undefined });
      if (cat === 'crypto')        this.loadCrypto().catch(() => { /* handled inside */ });
      if (cat === 'usStocks') {
        // Use `finally` (not `then`) so Polygon still loads even if Finnhub fails.
        const finnhubLoad = FINNHUB_KEY
          ? this.loadUSStocks()
          : Promise.resolve();
        finnhubLoad
          .catch(() => { /* handled inside loadUSStocks */ })
          .finally(() => {
            if (POLYGON_KEY) this.loadPolygonStocks().catch(() => { /* handled inside */ });
          });
      }
      if (cat === 'international') this.loadInternational().catch(() => { /* handled inside */ });
      if (cat === 'otc')           this.loadOTC().catch(() => { /* handled inside */ });
    } else {
      this.initialized = false;
      this.logged70k = false;
      this.data = { crypto: [], usStocks: [], international: [], otc: [] };
      this.init().catch(() => { /* handled inside */ });
    }
  }

  // ── Cache helpers ────────────────────────────────────────────────────────
  private async restoreFromCache(cat: CategoryKey): Promise<void> {
    const ttl = CACHE_TTL[cat];
    let cached = lsGet(cat, ttl);
    if (!cached) cached = await idbGet(cat, ttl);
    if (cached && cached.length > 0) {
      this.data[cat] = cached;
      this.updateCategory(cat, { state: 'complete', count: cached.length, lastUpdated: Date.now() });
    }
  }

  private async persistToCache(cat: CategoryKey, data: Security[]): Promise<void> {
    lsSet(cat, data);
    if (data.length > 5000) await idbSet(cat, data);
  }

  // ── CoinGecko – Crypto (~10 000 coins, no key needed) ────────────────────
  // Falls back to Coinpaprika automatically when CoinGecko rate-limits (HTTP 429).
  async loadCrypto(): Promise<void> {
    if (this.status.crypto.state === 'complete' && this.data.crypto.length >= this.MIN_CRYPTO_THRESHOLD) return;
    this.updateCategory('crypto', { state: 'loading', count: this.data.crypto.length });

    const allCoins: Security[] = [];
    let rateLimited = false;
    try {
      for (let page = 1; page <= this.COINGECKO_MAX_PAGES; page++) {
        let batch: Security[];
        try {
          batch = await this.fetchCoinGeckoPage(page);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          if (msg.includes('rate limit')) {
            rateLimited = true;
            break;
          }
          throw err;
        }
        if (batch.length === 0) break;

        allCoins.push(...batch);
        this.data.crypto = [...allCoins];
        this.updateCategory('crypto', {
          state: page < this.COINGECKO_MAX_PAGES && batch.length === 250 ? 'partial' : 'complete',
          count: allCoins.length,
        });

        if (page < this.COINGECKO_MAX_PAGES && batch.length === 250) {
          await this.sleep(this.COINGECKO_DELAY_MS);
        }
      }

      // If CoinGecko rate-limited before we got meaningful data, try Coinpaprika.
      if (rateLimited && allCoins.length < this.MIN_CRYPTO_THRESHOLD) {
        console.warn('[SecurityMaster] CoinGecko rate-limited, falling back to Coinpaprika');
        const paprika = await this.fetchCoinpaprika();
        const paprikaNew = paprika.filter(c => !allCoins.some(e => e.ticker === c.ticker));
        const combined = [...allCoins, ...paprikaNew];
        this.data.crypto = combined;
        await this.persistToCache('crypto', combined);
        this.updateCategory('crypto', { state: 'complete', count: combined.length, lastUpdated: Date.now() });
        return;
      }

      await this.persistToCache('crypto', allCoins);
      this.updateCategory('crypto', { state: 'complete', count: allCoins.length, lastUpdated: Date.now() });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Try Coinpaprika as a full fallback before marking the category as errored.
      try {
        console.warn('[SecurityMaster] CoinGecko failed, falling back to Coinpaprika:', msg);
        const paprika = await this.fetchCoinpaprika();
        if (paprika.length > 0) {
          this.data.crypto = paprika;
          await this.persistToCache('crypto', paprika);
          this.updateCategory('crypto', { state: 'complete', count: paprika.length, lastUpdated: Date.now() });
          return;
        }
      } catch (paprikaErr) {
        console.warn('[SecurityMaster] Coinpaprika fallback also failed:', paprikaErr);
      }
      this.updateCategory('crypto', {
        state: allCoins.length > 0 ? 'partial' : 'error',
        count: allCoins.length,
        error: msg,
      });
    }
  }

  private async fetchCoinpaprika(): Promise<Security[]> {
    // Coinpaprika public API – no key required, returns market data for all listed coins.
    const res = await fetch('https://api.coinpaprika.com/v1/tickers?quotes=USD');
    if (!res.ok) throw new Error(`Coinpaprika HTTP ${res.status}`);
    const raw = await res.json();
    if (!Array.isArray(raw)) throw new Error('Coinpaprika: unexpected response format');
    const coins = raw as CoinpaprikaTicker[];
    return coins.map(c => {
      const usd   = c.quotes?.USD;
      const pct   = Number(usd?.percent_change_24h) || 0;
      const price = Number(usd?.price)              || 0;
      return {
        ticker:        String(c.symbol ?? '').toUpperCase(),
        name:          String(c.name ?? ''),
        exchange:      'Crypto',
        lastTrade:     price,
        change:        price * pct / 100,
        changePercent: pct,
        volume:        Number(usd?.volume_24h)          || 0,
        sharesOut:     Number(c.circulating_supply)     || 0,
        marketCap:     Number(usd?.market_cap)          || 0,
        category:      'Crypto',
      };
    });
  }

  private async fetchCoinGeckoPage(page: number): Promise<Security[]> {
    const apiUrl =
      `https://api.coingecko.com/api/v3/coins/markets` +
      `?vs_currency=usd&order=market_cap_desc&per_page=250&page=${page}&sparkline=false`;

    // Try direct fetch first; fall back to a CORS proxy if the browser blocks it.
    let res: Response;
    try {
      res = await fetch(apiUrl);
    } catch (err) {
      // Network / CORS failure – log and retry via proxy
      console.warn('[SecurityMaster] CoinGecko direct fetch failed, retrying via proxy:', err);
      const proxyUrl = `${CORS_PROXY}${encodeURIComponent(apiUrl)}`;
      res = await fetch(proxyUrl);
    }

    if (res.status === 429) throw new Error('CoinGecko rate limit hit');
    if (!res.ok)            throw new Error(`CoinGecko HTTP ${res.status}`);
    const coins = await res.json() as Record<string, unknown>[];
    return coins.map(c => ({
      ticker:        String(c.symbol ?? '').toUpperCase(),
      name:          String(c.name ?? ''),
      exchange:      'Crypto',
      lastTrade:     Number(c.current_price)              || 0,
      change:        Number(c.price_change_24h)           || 0,
      changePercent: Number(c.price_change_percentage_24h)|| 0,
      volume:        Number(c.total_volume)               || 0,
      sharesOut:     Number(c.circulating_supply)         || 0,
      marketCap:     Number(c.market_cap)                 || 0,
      category:      'Crypto',
    }));
  }

  // ── Finnhub – US Stocks & ETFs (~10 000+, needs REACT_APP_FINNHUB_KEY) ───
  async loadUSStocks(): Promise<void> {
    if (!FINNHUB_KEY) return;
    if (this.status.usStocks.state === 'complete' && this.data.usStocks.length >= 1000) return;
    this.updateCategory('usStocks', { state: 'loading' });
    try {
      const res = await fetch(
        `https://finnhub.io/api/v1/stock/symbol?exchange=US&token=${FINNHUB_KEY}`,
      );
      if (!res.ok) throw new Error(`Finnhub HTTP ${res.status}`);
      const symbols = await res.json() as Record<string, unknown>[];
      const stocks: Security[] = symbols.map(s => ({
        ticker:        String(s.symbol ?? ''),
        name:          String(s.description ?? ''),
        exchange:      String(s.mic ?? s.exchange ?? 'US'),
        lastTrade:     0,
        change:        0,
        changePercent: 0,
        volume:        0,
        sharesOut:     0,
        marketCap:     0,
        category:      String(s.type) === 'ETF' ? 'ETF' : 'Stock',
      }));
      this.data.usStocks = stocks;
      await this.persistToCache('usStocks', stocks);
      this.updateCategory('usStocks', { state: 'complete', count: stocks.length, lastUpdated: Date.now() });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.updateCategory('usStocks', { state: 'error', error: msg });
    }
  }

  // ── Polygon.io – US Stocks & ETFs (~20 000+, needs REACT_APP_POLYGON_KEY) ─
  // Supplements Finnhub: merges new tickers not already loaded from Finnhub.
  // Note: Polygon's free-tier API requires the key as a query parameter;
  // header-based auth is only available on paid plans.
  async loadPolygonStocks(): Promise<void> {
    if (!POLYGON_KEY) return;
    this.updateCategory('usStocks', { state: 'loading' });
    const seen = new Set(this.data.usStocks.map(s => s.ticker));
    const added: Security[] = [];
    try {
      let nextUrl: string | null =
        `https://api.polygon.io/v3/reference/tickers` +
        `?market=stocks&active=true&limit=1000&apiKey=${POLYGON_KEY}`;
      while (nextUrl) {
        const res = await fetch(nextUrl);
        if (!res.ok) throw new Error(`Polygon HTTP ${res.status}`);
        const json = await res.json() as {
          results?: Record<string, unknown>[];
          next_url?: string;
        };
        const results = json.results ?? [];
        for (const t of results) {
          const ticker = String(t.ticker ?? '');
          if (!seen.has(ticker)) {
            seen.add(ticker);
            added.push({
              ticker,
              name:          String(t.name ?? ''),
              exchange:      String(t.primary_exchange ?? t.market ?? 'US'),
              lastTrade:     0,
              change:        0,
              changePercent: 0,
              volume:        0,
              sharesOut:     0,
              marketCap:     0,
              category:      String(t.type) === 'ETF' ? 'ETF' : 'Stock',
            });
          }
        }
        // Append apiKey to the pagination URL provided by Polygon
        nextUrl = json.next_url
          ? `${json.next_url}&apiKey=${POLYGON_KEY}`
          : null;
        // Emit incremental progress
        if (added.length > 0) {
          this.data.usStocks = [...this.data.usStocks, ...added.splice(0)];
          this.updateCategory('usStocks', {
            state: nextUrl ? 'partial' : 'complete',
            count: this.data.usStocks.length,
          });
        }
        if (nextUrl) await this.sleep(200); // stay within free-tier rate limits
      }
      await this.persistToCache('usStocks', this.data.usStocks);
      this.updateCategory('usStocks', { state: 'complete', count: this.data.usStocks.length, lastUpdated: Date.now() });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Preserve any stocks already loaded; only mark error if nothing was added
      this.updateCategory('usStocks', {
        state: this.data.usStocks.length > 0 ? 'partial' : 'error',
        error: msg,
      });
    }
  }

  // ── Twelve Data – International equities (~40 000+, needs REACT_APP_TWELVE_DATA_KEY) ──
  async loadInternational(): Promise<void> {
    if (!TWELVE_DATA_KEY) return;
    if (this.status.international.state === 'complete' && this.data.international.length >= 1000) return;
    this.updateCategory('international', { state: 'loading' });
    try {
      const res = await fetch(
        `https://api.twelvedata.com/stocks?apikey=${TWELVE_DATA_KEY}&source=docs`,
      );
      if (!res.ok) throw new Error(`Twelve Data HTTP ${res.status}`);
      const json = await res.json() as { data?: Record<string, unknown>[] };
      const all  = json.data ?? [];
      // Deduplicate by ticker symbol: many companies trade on multiple exchanges,
      // which inflates the raw count to 176 000+. Keeping the first occurrence
      // (typically the primary/largest exchange returned by Twelve Data) collapses
      // the list to the ~40 000–50 000 unique tickers range.
      const seenTickers = new Set<string>();
      const intl: Security[] = [];
      for (const s of all) {
        if (s.country === 'United States') continue;
        const ticker = String(s.symbol ?? '');
        if (seenTickers.has(ticker)) continue;
        seenTickers.add(ticker);
        intl.push({
          ticker,
          name:          String(s.name ?? ''),
          exchange:      String(s.exchange ?? s.country ?? 'International'),
          lastTrade:     0,
          change:        0,
          changePercent: 0,
          volume:        0,
          sharesOut:     0,
          marketCap:     0,
          category:      'International',
        });
      }
      this.data.international = intl;
      await this.persistToCache('international', intl);
      this.updateCategory('international', { state: 'complete', count: intl.length, lastUpdated: Date.now() });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.updateCategory('international', { state: 'error', error: msg });
    }
  }

  // ── FMP – OTC securities (~10 000+, needs REACT_APP_FMP_KEY) ─────────────
  async loadOTC(): Promise<void> {
    if (!FMP_KEY) return;
    if (this.status.otc.state === 'complete' && this.data.otc.length >= 1000) return;
    this.updateCategory('otc', { state: 'loading' });
    try {
      const res = await fetch(
        `https://financialmodelingprep.com/api/v3/available-traded/list?apikey=${FMP_KEY}`,
      );
      if (!res.ok) throw new Error(`FMP HTTP ${res.status}`);
      const list = await res.json() as Record<string, unknown>[];
      const otc: Security[] = list
        .filter(s => {
          const exch = String(s.exchangeShortName ?? s.exchange ?? '').toUpperCase();
          return exch === 'OTC' || exch === 'PINK' || exch === 'PNK' || exch.includes('OTC');
        })
        .map(s => ({
          ticker:        String(s.symbol ?? ''),
          name:          String(s.name ?? ''),
          exchange:      String(s.exchange ?? 'OTC'),
          lastTrade:     Number(s.price)     || 0,
          change:        0,
          changePercent: 0,
          volume:        0,
          sharesOut:     0,
          marketCap:     Number(s.marketCap) || 0,
          category:      'OTC',
        }));
      this.data.otc = otc;
      await this.persistToCache('otc', otc);
      this.updateCategory('otc', { state: 'complete', count: otc.length, lastUpdated: Date.now() });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this.updateCategory('otc', { state: 'error', error: msg });
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(r => setTimeout(r, ms));
  }
}

/** Singleton loader – call `.init()` once, then `.subscribe()` for reactive updates. */
export const securityMasterLoader = new SecurityMasterLoader();

/** Backward-compatible static export – resolves to the seed data.
 *  New code should use `securityMasterLoader` or `useSecurityMaster` hook. */
export const securities: Security[] = SEED_SECURITIES;

export default securities;
